/** /auth/mfa — verifies the second factor after a 202 from /login/.
 *
 *  "Use a backup code" (dashboard password MFA plan OD12) swaps the code field for a
 *  backup-code field and verifies with `method: "backup_code"`, which the backend accepts
 *  whatever `methods` the pending token lists. Only the numeric field is a one-time code for
 *  autofill. */
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  Link as MuiLink,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import type { LoginResponse202 } from '@/api/auth';
import { ConsoleApiError } from '@/api/client';
import * as mfaApi from '@/api/mfa';

import { useAuth } from './AuthProvider';
import { sanitizeReturnTo } from './returnTo';

interface RouterState {
  pending?: LoginResponse202;
  from?: { pathname: string } | null;
  /** Post-verify destination, resolved by LoginPage (incl. grid filters). */
  returnTo?: string | null;
}

export function MFAChallengePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as RouterState;

  const pending = state.pending;
  const [method, setMethod] = useState<'totp' | 'sms' | 'email'>(
    pending?.default_method ?? 'totp',
  );
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!pending) {
    return (
      <Container maxWidth="xs" sx={{ pt: 8 }}>
        <Alert severity="warning">No pending MFA challenge. Please sign in again.</Alert>
        <Box sx={{ mt: 2 }}>
          <MuiLink component={RouterLink} to="/auth/login" underline="hover">
            Back to sign-in
          </MuiLink>
        </Box>
      </Container>
    );
  }

  async function sendChallenge() {
    if (!pending) return;
    if (method === 'totp') {
      setInfo('Open your authenticator app and enter the current code.');
      return;
    }
    setBusy(true);
    setServerError(null);
    try {
      const r = await mfaApi.challenge(pending.mfa_pending_token, method);
      setInfo(
        r.sent_to_masked ? `Code sent to ${r.sent_to_masked}.` : 'Code sent.',
      );
    } catch (e) {
      setServerError(e instanceof ConsoleApiError ? e.message : 'Failed to send code.');
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (!pending) return;
    setBusy(true);
    setServerError(null);
    try {
      const r = await mfaApi.verify(
        pending.mfa_pending_token,
        backup ? 'backup_code' : method,
        code.trim(),
      );
      await auth.acceptSession(r.token, r.token_expires);
      const dest = sanitizeReturnTo(state.returnTo) ?? state.from?.pathname ?? '/';
      navigate(dest, { replace: true });
    } catch (e) {
      setServerError(e instanceof ConsoleApiError ? e.message : 'Verification failed.');
    } finally {
      setBusy(false);
    }
  }

  function toggleBackup() {
    setBackup((b) => !b);
    // A half-typed code of one kind is never sent as the other.
    setCode('');
    setInfo(null);
    setServerError(null);
  }

  return (
    <Container maxWidth="xs" sx={{ pt: 8 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Two-factor verification
      </Typography>

      {!backup && pending.methods.length > 1 && (
        <FormControl sx={{ mb: 2 }}>
          <RadioGroup
            row
            value={method}
            onChange={(e) => setMethod(e.target.value as 'totp' | 'sms' | 'email')}
          >
            {pending.methods.map((m) => (
              <FormControlLabel key={m} value={m} control={<Radio />} label={m.toUpperCase()} />
            ))}
          </RadioGroup>
        </FormControl>
      )}

      <Stack spacing={2}>
        {!backup && method !== 'totp' && (
          <Button variant="outlined" onClick={sendChallenge} disabled={busy}>
            Send code
          </Button>
        )}
        {backup ? (
          <TextField
            key="backup"
            label="Backup code"
            autoComplete="off"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            helperText="One of the single-use codes you saved when you set up two-factor sign-in."
            inputProps={{ autoCapitalize: 'characters', spellCheck: false }}
          />
        ) : (
          <TextField
            key="code"
            label="Verification code"
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.trim())}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
        )}
        {info && <Alert severity="info">{info}</Alert>}
        {serverError && <Alert severity="error">{serverError}</Alert>}
        <Button variant="contained" onClick={verify} disabled={busy || code.trim().length === 0}>
          Verify
        </Button>
        <MuiLink
          component="button"
          type="button"
          onClick={toggleBackup}
          underline="hover"
          sx={{ alignSelf: 'flex-start' }}
        >
          {backup ? 'Use a verification code instead' : 'Use a backup code'}
        </MuiLink>
        <MuiLink component={RouterLink} to="/auth/login" underline="hover">
          Restart sign-in
        </MuiLink>
      </Stack>
    </Container>
  );
}
